import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../services/api';
import {
  emptyProgress,
  readProgress,
  writeProgress,
  recordViewed,
  type ReviewProgress,
} from './durcharbeite-progress';
import { useNavigate } from '@tanstack/react-router';
import { Button, LoadingSpinner, EmptyState } from '../ui';
import {
  useDurcharbeiteQueue,
  useDurcharbeiteItem,
  useDurcharbeiteSaveAndNext,
  type DurcharbeiteItemDto,
  type DurcharbeiteQueuePage,
} from '../../services/api/durcharbeite';
import { useExpenseAttachments } from '../../services/api/attachments';
import { ExpenseAttachmentMultiPagePreview } from './ExpenseAttachmentMultiPagePreview';
import { ExpenseForm } from './ExpenseForm';
import {
  expenseDraft,
  expenseDraftError,
  expensePatch,
  reconcileExpenseDraft,
  type ExpenseDraft,
} from './expense-draft';
import { useUpdateExpense } from '../../services/api/expenses';
import { useToastHelpers } from '../ui/Toast';
import { useUnsavedChanges, hasUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useClosedQuarterConfirmation } from '../../hooks/useClosedQuarterConfirmation';
import { apiErrorMessage } from '../../lib/api-error-message';
import { formatCurrency } from '@armard/shared';
import { formatDateBerlin } from '../../lib/format-date';

export function DurcharbeiteView({
  activeId,
  onNavigate,
  contextLabel,
  progressStorageKey,
}: {
  contextLabel?: string;
  progressStorageKey?: string;
  activeId: string | undefined;
  onNavigate: (id: string | null) => void;
}) {
  const [progress, setProgress] = useState(() => readProgress(progressStorageKey));
  const [stored, setStored] = useState(true);
  const recordedId = useRef<string | undefined>(undefined);
  const resolvedId = activeId ?? progress.lastId ?? undefined;
  const queue = useDurcharbeiteQueue({ page: 1, limit: 100 });
  const item = useDurcharbeiteItem(resolvedId);
  const navigate = useNavigate();
  const unsaved = hasUnsavedChanges();
  const staleItem =
    !unsaved &&
    ((item.error instanceof ApiError && item.error.status === 404) ||
      (item.data && !item.data.inQueue));
  useEffect(() => {
    if (!queue.data || item.isLoading) return;
    if (!resolvedId || staleItem) {
      const nextId = queue.data.items[0]?.id ?? null;
      if (staleItem && progress.lastId !== nextId) {
        const next = { ...progress, lastId: nextId };
        setProgress(next);
        setStored(writeProgress(progressStorageKey, next));
      }
      if (nextId !== activeId && (nextId || activeId)) onNavigate(nextId);
    } else if (!activeId && !item.error) onNavigate(resolvedId);
  }, [
    resolvedId,
    activeId,
    queue.data,
    item.isLoading,
    staleItem,
    item.error,
    onNavigate,
    progress,
    progressStorageKey,
  ]);
  useEffect(() => {
    if (!item.data?.inQueue || item.data.expense.id !== resolvedId) return;
    if (recordedId.current === resolvedId) return;
    recordedId.current = resolvedId;
    const next = recordViewed(progress, resolvedId!);
    setProgress(next);
    setStored(writeProgress(progressStorageKey, next));
  }, [item.data, resolvedId, progressStorageKey, progress]);
  if (queue.isLoading || (resolvedId && item.isLoading))
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  if (
    (queue.error && !(unsaved && queue.data)) ||
    (item.error && !staleItem && !(unsaved && item.data))
  )
    return (
      <EmptyState
        title="Belege konnten nicht geladen werden"
        description={apiErrorMessage(queue.error ?? item.error)}
        action={{
          label: 'Erneut laden',
          onClick: () => {
            void queue.refetch();
            void item.refetch();
          },
        }}
      />
    );
  if (!queue.data?.pagination.total && !unsaved)
    return (
      <div className="space-y-4">
        <EmptyState
          title="Keine Belege in der Prüfwarteschlange"
          description="Es stehen keine weiteren Belege zur Prüfung an. Angesehen bedeutet keine fachliche Freigabe."
          action={{
            label: 'Zurück zu Ausgaben',
            onClick: () => {
              void navigate({ to: '/expenses' });
            },
          }}
        />
        <p className="text-sm text-white/60">
          {progress.seenIds.length} {progress.seenIds.length === 1 ? 'Beleg' : 'Belege'} angesehen ·
          keine fachliche Freigabe
        </p>
        <Button
          variant="ghost"
          onClick={() => {
            const next = emptyProgress();
            setProgress(next);
            setStored(writeProgress(progressStorageKey, next));
          }}
        >
          Ansichtsfortschritt zurücksetzen
        </Button>
      </div>
    );
  if (staleItem || !item.data || !queue.data) return <LoadingSpinner />;
  return (
    <ReviewExpense
      key={item.data.expense.id}
      item={item.data}
      queue={queue.data}
      onNavigate={onNavigate}
      contextLabel={contextLabel}
      progress={progress}
      progressStored={stored}
      resetProgress={() => {
        const next = emptyProgress();
        setProgress(next);
        setStored(writeProgress(progressStorageKey, next));
      }}
      refreshQueue={async () => {
        await queue.refetch();
      }}
    />
  );
}
function ReviewExpense({
  item,
  queue,
  onNavigate,
  refreshQueue,
  contextLabel,
  progress,
  progressStored,
  resetProgress,
}: {
  contextLabel?: string;
  progress: ReviewProgress;
  progressStored: boolean;
  resetProgress: () => void;
  item: DurcharbeiteItemDto;
  queue: DurcharbeiteQueuePage;
  onNavigate: (id: string | null) => void;
  refreshQueue: () => Promise<void>;
}) {
  const expense = item.expense;
  const server = useRef(expense);
  const [draft, setDraft] = useState<ExpenseDraft>(() => expenseDraft(expense));
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const guard = useUnsavedChanges(draft);
  const confirmation = useClosedQuarterConfirmation();
  const update = useUpdateExpense(expense.id);
  const promote = useDurcharbeiteSaveAndNext();
  const attachments = useExpenseAttachments(expense.id);
  const navigate = useNavigate();
  const toast = useToastHelpers();
  function cancelTimer() {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }
  useEffect(() => () => cancelTimer(), []);
  /** One writer owns the current revision. New edits during a save are flushed in order. */
  function flush(): Promise<boolean> {
    cancelTimer();
    if (inFlight.current) return inFlight.current;
    const work = (async () => {
      setSaving(true);
      setSaveError(null);
      try {
        while (true) {
          const snapshot = latestDraft.current;
          const patch = expensePatch(server.current, snapshot);
          if (!patch) return true;
          const invalid = expenseDraftError(snapshot);
          if (invalid) {
            setSaveError(invalid);
            return false;
          }
          const saved = await confirmation.run((confirmed) =>
            update.mutateAsync({ ...patch, confirmNachträglich: confirmed })
          );
          if (!saved) return false;
          const canonicalDraft = expenseDraft(saved);
          const reconciledDraft = reconcileExpenseDraft(snapshot, latestDraft.current, saved);
          server.current = saved;
          latestDraft.current = reconciledDraft;
          setDraft(reconciledDraft);
          guard.markSaved(canonicalDraft);
        }
      } catch (error) {
        setSaveError(apiErrorMessage(error));
        return false;
      } finally {
        setSaving(false);
      }
    })();
    inFlight.current = work.finally(() => {
      inFlight.current = null;
    });
    return inFlight.current;
  }
  function change(next: ExpenseDraft) {
    latestDraft.current = next;
    setDraft(next);
    cancelTimer();
    if (expensePatch(server.current, next))
      timer.current = setTimeout(() => {
        void flush();
      }, 1500);
  }
  async function go(id: string | null, exit = false) {
    if (leaving) return;
    setLeaving(true);
    if (await flush()) {
      if (exit) await navigate({ to: '/expenses' });
      else onNavigate(id);
    }
    setLeaving(false);
  }
  async function openDetail() {
    if (leaving) return;
    setLeaving(true);
    if (await flush()) await navigate({ to: '/expenses/$id', params: { id: expense.id } });
    setLeaving(false);
  }
  async function saveAndNext(promoteTo: 'manual' | 'validated') {
    if (leaving) return;
    setLeaving(true);
    if (!(await flush())) {
      setLeaving(false);
      return;
    }
    try {
      const result = await promote.mutateAsync({
        id: expense.id,
        patch: { version: server.current.version },
        promoteTo,
      });
      guard.markSaved();
      await refreshQueue();
      toast.success(
        result.nextId ? 'Beleg geprüft und gespeichert' : 'Alle Belege durchgearbeitet'
      );
      onNavigate(result.nextId);
    } catch (error) {
      setSaveError(apiErrorMessage(error));
    } finally {
      setLeaving(false);
    }
  }
  async function resetViewed() {
    if (leaving) return;
    setLeaving(true);
    if (await flush()) resetProgress();
    setLeaving(false);
  }
  const progressContent = (
    <div className="space-y-1 text-xs text-white/60" data-testid="durcharbeite-progress">
      <p>
        {progress.seenIds.length} {progress.seenIds.length === 1 ? 'Beleg' : 'Belege'} angesehen ·
        keine fachliche Freigabe
      </p>
      <p>
        {progressStored
          ? 'Fortschritt wird in diesem Browser gespeichert.'
          : 'Fortschritt kann in diesem Browser nicht gespeichert werden.'}
      </p>
      {progress.updatedAt && <p>Zuletzt angesehen: {formatDateBerlin(progress.updatedAt)}</p>}
      <Button variant="ghost" size="sm" disabled={leaving} onClick={() => void resetViewed()}>
        Ansichtsfortschritt zurücksetzen
      </Button>
    </div>
  );
  const navigation = item.navigation;
  const keyboard = useRef({ go, navigation });
  keyboard.current = { go, navigation };
  useEffect(() => {
    function key(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        target?.closest('input,textarea,select,[contenteditable="true"],[role="dialog"]')
      )
        return;
      const nav = keyboard.current.navigation;
      const id =
        event.key === 'ArrowLeft' ? nav.prevId : event.key === 'ArrowRight' ? nav.nextId : null;
      if (id) {
        event.preventDefault();
        void keyboard.current.go(id);
      }
    }
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);
  return (
    <div className="flex flex-col md:flex-row h-dvh bg-black" data-testid="durcharbeite-view">
      {confirmation.dialog}
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-zinc-950">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-lg text-white">Belege prüfen</h2>
          {progressContent}
          {contextLabel && <p className="mt-1 text-xs text-white/70">{contextLabel}</p>}
          <p className="text-xs text-white/50 mt-1">{queue.pagination.total} Belege offen</p>
        </div>
        <ul className="overflow-y-auto flex-1 divide-y divide-white/5">
          {queue.items.map((row) => (
            <li key={row.id}>
              <button
                disabled={leaving}
                onClick={() => void go(row.id)}
                className={`w-full text-left px-4 py-3 text-sm ${row.id === expense.id ? 'bg-blue-500/10 text-white' : 'text-white/70 hover:bg-white/5'}`}
                data-testid={`durcharbeite-queue-item-${row.id}`}
              >
                <div className="truncate">
                  {row.supplierInvoiceNumber ?? 'Ohne Rechnungsnummer'}
                  {progress.seenIds.includes(row.id) && (
                    <span className="ml-2 text-xs text-white/50">Angesehen</span>
                  )}
                </div>
                <div className="text-xs text-white/50 mt-1">
                  {row.invoiceDate ? formatDateBerlin(row.invoiceDate) : 'Datum fehlt'} ·{' '}
                  {formatCurrency(Number(row.grossTotal))}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <main className="flex-1 flex flex-col min-w-0 min-h-0" data-testid="durcharbeite-main">
        <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-zinc-950">
          <Button variant="ghost" onClick={() => void go(null, true)} disabled={leaving}>
            ← Ausgaben
          </Button>
          {contextLabel && <span className="text-xs text-white/60 md:hidden">{contextLabel}</span>}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              aria-label="Vorheriger Beleg"
              onClick={() => navigation.prevId && void go(navigation.prevId)}
              disabled={!navigation.prevId || leaving}
              data-testid="durcharbeite-prev"
            >
              ←
            </Button>
            <span className="text-sm text-white/70" data-testid="durcharbeite-position">
              {navigation.queuePosition || '—'} / {navigation.queueTotal}
            </span>
            <Button
              variant="ghost"
              aria-label="Nächster Beleg"
              onClick={() => navigation.nextId && void go(navigation.nextId)}
              disabled={!navigation.nextId || leaving}
              data-testid="durcharbeite-next"
            >
              →
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => void saveAndNext('validated')}
              disabled={leaving}
              data-testid="durcharbeite-confirm-as-extracted"
            >
              Als korrekt bestätigen
            </Button>
            <Button
              onClick={() => void saveAndNext('manual')}
              disabled={leaving}
              data-testid="durcharbeite-save-next"
            >
              {leaving ? 'Speichern…' : 'Speichern + weiter'}
            </Button>
          </div>
        </header>
        <div className="flex-1 overflow-auto md:overflow-hidden flex flex-col md:flex-row min-h-0">
          <div className="md:w-1/2 md:overflow-auto border-b md:border-b-0 md:border-r border-white/10 p-2">
            {attachments.isLoading ? (
              <LoadingSpinner />
            ) : attachments.error ? (
              <div role="alert" className="p-6 text-sm text-white/70">
                <p>Die Originale konnten nicht geladen werden.</p>
                <Button variant="ghost" onClick={() => void attachments.refetch()}>
                  Erneut laden
                </Button>
              </div>
            ) : attachments.data?.items.length ? (
              <ExpenseAttachmentMultiPagePreview
                attachments={attachments.data.items}
                heightClass="h-[50vh] md:h-[70vh]"
                testid="durcharbeite-preview"
                enableKeyboard={false}
              />
            ) : (
              <p className="p-6 text-sm text-white/50">Kein Beleg-Anhang vorhanden.</p>
            )}
          </div>
          <section className="md:w-1/2 md:overflow-y-auto p-4 space-y-4 bg-zinc-950">
            <div className="md:hidden">{progressContent}</div>
            <div aria-live="polite" className="text-sm text-white/60">
              {saving
                ? 'Wird gespeichert…'
                : guard.isDirty
                  ? 'Ungespeicherte Änderungen'
                  : 'Alle Änderungen gespeichert'}
            </div>
            {saveError && (
              <div
                role="alert"
                className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-200 text-sm"
              >
                <p>{saveError}</p>
                <Button variant="ghost" size="sm" onClick={() => void flush()}>
                  Erneut speichern
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-white/60">
              <span>
                Belegstatus:{' '}
                {(
                  {
                    draft: 'Entwurf',
                    neu: 'Neu',
                    geprüft: 'Geprüft',
                    rückfrage_offen: 'Rückfrage offen',
                    nicht_relevant: 'Nicht relevant',
                    zurückgezogen: 'Zurückgezogen',
                    archiviert: 'Archiviert',
                  } as Record<string, string>
                )[expense.status] ?? expense.status}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={leaving}
                onClick={() => void openDetail()}
              >
                Details und Statusaktionen
              </Button>
            </div>
            <ExpenseForm
              mode="edit"
              initial={expense}
              draftOverride={draft}
              submitting={leaving}
              onSubmit={() => void flush()}
              submitLabel="Speichern"
              cancelHref="/expenses"
              onDraftChange={change}
              hideActions
            />
          </section>
        </div>
      </main>
    </div>
  );
}
